import {
  Alert,
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Rating,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material'
import { Dialog } from '@/components/AppDialog'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type ReactElement } from 'react'
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined'
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined'
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline'
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined'
import StarOutlineIcon from '@mui/icons-material/StarOutline'
import { useNavigate } from 'react-router-dom'
import { ApiError } from '@/api/client'
import DownloadIcon from '@mui/icons-material/Download'
import { AvailabilityBoard, today } from './AvailabilityBoard'
import { PlanEventDialog } from './PlanEventDialog'
import { formatDate, formatMoney } from '@/components/common'
import {
  openPortalPdf,
  portalApi,
  portalSession,
  type PortalEnquiry,
  type PortalFeedback,
  type PortalInvoice,
  type PortalOrder,
  type PortalPayment,
  type PortalQuotation,
  type PortalService,
  type PortalSummary,
} from './portalApi'

const TABS = ['Overview', 'Availability', 'Enquiries', 'Quotations', 'Orders', 'Bills', 'Payments', 'Feedback']

const TAB_ICONS: Record<string, ReactElement> = {
  Overview: <HomeOutlinedIcon fontSize="small" />,
  Availability: <EventAvailableOutlinedIcon fontSize="small" />,
  Enquiries: <ChatBubbleOutlineIcon fontSize="small" />,
  Quotations: <RequestQuoteOutlinedIcon fontSize="small" />,
  Orders: <ReceiptLongOutlinedIcon fontSize="small" />,
  Bills: <DescriptionOutlinedIcon fontSize="small" />,
  Payments: <PaymentsOutlinedIcon fontSize="small" />,
  Feedback: <StarOutlineIcon fontSize="small" />,
}

/**
 * A customer's own file: what they have asked for, what we quoted, what is
 * booked, what is billed and what they have paid.
 *
 * <p>Everything here comes back scoped to them by the server. Nothing on this
 * screen sends a customer id, because nothing is allowed to choose one.</p>
 */
export function CustomerPortalPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [tab, setTab] = useState(0)
  // Named rather than numbered: a tab inserted in the middle used to shift
  // every screen below it onto the wrong index.
  const showing = TABS[tab]
  const [planning, setPlanning] = useState(false)
  const [availabilityDate, setAvailabilityDate] = useState(today())
  const [availabilityService, setAvailabilityService] = useState('')
  const [deciding, setDeciding] = useState<PortalQuotation | null>(null)
  const [rating, setRating] = useState<PortalOrder | null>(null)

  const signOut = () => {
    portalSession.clear()
    queryClient.clear()
    navigate('/my/login', { replace: true })
  }

  const summary = useQuery({
    queryKey: ['portal', 'summary'],
    queryFn: () => portalApi.get<PortalSummary>('/summary'),
  })
  const enquiries = useQuery({
    queryKey: ['portal', 'enquiries'],
    queryFn: () => portalApi.get<PortalEnquiry[]>('/enquiries'),
    enabled: showing === 'Enquiries',
  })
  const quotations = useQuery({
    queryKey: ['portal', 'quotations'],
    queryFn: () => portalApi.get<PortalQuotation[]>('/quotations'),
    enabled: showing === 'Quotations' || showing === 'Overview',
  })
  const feedback = useQuery({
    queryKey: ['portal', 'feedback'],
    queryFn: () => portalApi.get<PortalFeedback[]>('/feedback'),
    enabled: showing === 'Feedback',
  })
  const orders = useQuery({
    queryKey: ['portal', 'orders'],
    queryFn: () => portalApi.get<PortalOrder[]>('/orders'),
    enabled: showing === 'Orders',
  })
  const invoices = useQuery({
    queryKey: ['portal', 'invoices'],
    queryFn: () => portalApi.get<PortalInvoice[]>('/invoices'),
    enabled: showing === 'Bills',
  })
  const payments = useQuery({
    queryKey: ['portal', 'payments'],
    queryFn: () => portalApi.get<PortalPayment[]>('/payments'),
    enabled: showing === 'Payments',
  })

  // Only the services held in a space of ours have a diary to show. An outdoor
  // job books nothing here, so offering it on this screen would answer nothing.
  const services = useQuery({
    queryKey: ['portal', 'services'],
    queryFn: () => portalApi.get<PortalService[]>('/services'),
    enabled: showing === 'Availability',
  })
  const venueServices = (services.data ?? []).filter((service) => service.spaceType)
  const shownService = availabilityService || venueServices[0]?.code || ''

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['portal'] })

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar position="sticky" elevation={0} sx={{ background: 'linear-gradient(135deg, #7a2030 0%, #a94356 100%)' }}>
        <Toolbar sx={{ gap: 1 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.22)', width: 36, height: 36 }}>
            {(summary.data?.customerName ?? portalSession.name() ?? '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>Welcome back</Typography>
            <Typography variant="h6" noWrap sx={{ lineHeight: 1.2 }}>
              {summary.data?.customerName ?? portalSession.name() ?? 'Your events'}
            </Typography>
          </Box>
          <Button color="inherit" size="small" onClick={() => navigate('/my/password')}>
            Password
          </Button>
          <Button color="inherit" size="small" variant="outlined" onClick={signOut}
            sx={{ borderColor: 'rgba(255,255,255,0.5)' }}>
            Sign out
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1100, mx: 'auto' }}>
        {summary.data ? (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Metric label="Open enquiries" value={String(summary.data.openEnquiries)} />
            <Metric
              label="Waiting on you"
              value={String(summary.data.quotationsToDecide)}
              hint={summary.data.quotationsToDecide > 0 ? 'Quotations to accept' : undefined}
            />
            <Metric label="Upcoming events" value={String(summary.data.upcomingEvents)} />
            <Metric label="Outstanding" value={formatMoney(summary.data.outstanding)} />
          </Grid>
        ) : null}

        <Card>
          <Tabs
            value={tab}
            onChange={(_event, value) => setTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ borderBottom: 1, borderColor: 'divider', minHeight: 52 }}
          >
            {TABS.map((label) => (
              <Tab key={label} label={label} icon={TAB_ICONS[label]} iconPosition="start" sx={{ minHeight: 52, textTransform: 'none', fontWeight: 600 }} />
            ))}
          </Tabs>
          <CardContent>
            {showing === 'Overview' ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="subtitle2">Your next events</Typography>
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" size="small" onClick={() => setPlanning(true)}>
                      Plan an event
                    </Button>
                  </Stack>
                </Stack>
                {summary.data?.upcoming?.length ? (
                  <Rows
                    headers={['Reference', 'Event', 'Date', 'Guests', 'Status']}
                    rows={summary.data.upcoming.map((event) => [
                      event.reference,
                      event.eventType,
                      formatDate(event.eventDate),
                      String(event.guestCount),
                      <Chip key="s" size="small" label={event.status} />,
                    ])}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nothing booked yet. Plan an event to get started.
                  </Typography>
                )}
              </>
            ) : null}

            {showing === 'Availability' ? (
              <>
                <Typography variant="subtitle2" gutterBottom>
                  Which sessions are free
                </Typography>
                {venueServices.length > 1 ? (
                  <TextField
                    select
                    size="small"
                    label="For"
                    value={shownService}
                    onChange={(event) => setAvailabilityService(event.target.value)}
                    sx={{ mb: 2, mr: 2, minWidth: 200 }}
                  >
                    {venueServices.map((service) => (
                      <MenuItem key={service.code} value={service.code}>
                        {service.name}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : null}
                {shownService ? (
                  <AvailabilityBoard
                    date={availabilityDate}
                    serviceType={shownService}
                    onDateChange={setAvailabilityDate}
                  />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Nothing of ours is booked by the session. Please call the office.
                  </Typography>
                )}
              </>
            ) : null}

            {showing === 'Enquiries' ? (
              <Rows
                headers={['Number', 'Event', 'Date', 'Guests', 'Status', '']}
                empty="You have not made any enquiries yet."
                rows={(enquiries.data ?? []).map((enquiry) => [
                  enquiry.enquiryNumber,
                  enquiry.eventType,
                  formatDate(enquiry.eventDate),
                  String(enquiry.guestCount),
                  <Chip key="s" size="small" label={enquiry.status} />,
                  enquiry.raisedByYou ? (
                    <Chip key="r" size="small" variant="outlined" label="Raised by you" />
                  ) : (
                    ''
                  ),
                ])}
              />
            ) : null}

            {showing === 'Quotations' ? (
              <Rows
                headers={['Number', 'Event', 'Date', 'Guests', 'Amount', 'Status', '']}
                empty="No quotations yet."
                rows={(quotations.data ?? []).map((quotation) => [
                  quotation.quotationNumber,
                  quotation.eventType,
                  formatDate(quotation.eventDate),
                  String(quotation.guestCount),
                  formatMoney(quotation.totalAmount),
                  <Chip key="s" size="small" label={quotation.status} />,
                  <Stack key="a" direction="row" spacing={1} justifyContent="flex-end">
                    <Button
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={() => void openPortalPdf(`/quotations/${quotation.id}/pdf`)}
                    >
                      Download
                    </Button>
                    {quotation.awaitingYourDecision ? (
                      <Button size="small" variant="contained" onClick={() => setDeciding(quotation)}>
                        Accept or decline
                      </Button>
                    ) : null}
                  </Stack>,
                ])}
              />
            ) : null}

            {showing === 'Orders' ? (
              <Rows
                headers={['Number', 'Event', 'Date', 'Guests', 'Total', 'Paid', 'Balance', '']}
                empty="No confirmed events yet."
                rows={(orders.data ?? []).map((order) => [
                  order.orderNumber,
                  order.eventType,
                  formatDate(order.eventDate),
                  String(order.guestCount),
                  formatMoney(order.totalAmount),
                  formatMoney(order.paidAmount),
                  formatMoney(order.balanceAmount),
                  new Date(order.eventDate) <= new Date() ? (
                    <Button key="f" size="small" onClick={() => setRating(order)}>
                      Give feedback
                    </Button>
                  ) : (
                    <Chip key="s" size="small" label={order.status} />
                  ),
                ])}
              />
            ) : null}

            {showing === 'Feedback' ? (
              <Rows
                headers={['Number', 'Event', 'Date', 'Overall', 'Food', 'Service', 'Staff', 'Your words']}
                empty="You have not given any feedback yet. After an event you can rate it from the Orders tab."
                rows={(feedback.data ?? []).map((item) => [
                  item.orderNumber,
                  item.eventType,
                  formatDate(item.eventDate),
                  <Stack key="o" direction="row" spacing={0.5} alignItems="center">
                    <Rating value={item.overallRating} precision={0.5} size="small" readOnly />
                    <Typography variant="caption">{item.overallRating.toFixed(1)}</Typography>
                  </Stack>,
                  item.foodQuality ? `${item.foodQuality} / 5` : '—',
                  item.service ? `${item.service} / 5` : '—',
                  item.staffBehaviour ? `${item.staffBehaviour} / 5` : '—',
                  item.comments || '—',
                ])}
              />
            ) : null}

            {showing === 'Bills' ? (
              <Rows
                headers={['Number', 'Date', 'Due', 'Amount', 'Outstanding', 'Status']}
                empty="No bills yet."
                rows={(invoices.data ?? []).map((invoice) => [
                  invoice.invoiceNumber,
                  formatDate(invoice.invoiceDate),
                  invoice.dueDate ? formatDate(invoice.dueDate) : '—',
                  formatMoney(invoice.totalAmount),
                  formatMoney(invoice.balanceAmount),
                  <Chip key="s" size="small" label={invoice.status} />,
                ])}
              />
            ) : null}

            {showing === 'Payments' ? (
              <Rows
                headers={['Receipt', 'Date', 'Amount', 'How', 'Reference']}
                empty="No payments recorded yet."
                rows={(payments.data ?? []).map((payment) => [
                  payment.receiptNumber,
                  formatDate(payment.paymentDate),
                  formatMoney(payment.amount),
                  payment.mode,
                  payment.reference ?? '—',
                ])}
              />
            ) : null}
          </CardContent>
        </Card>
      </Box>

      <PlanEventDialog open={planning} onClose={() => setPlanning(false)} onDone={refresh} />
      <DecideDialog quotation={deciding} onClose={() => setDeciding(null)} onDone={refresh} />
      <FeedbackDialog order={rating} onClose={() => setRating(null)} onDone={refresh} />
    </Box>
  )
}

function Metric({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <Grid item xs={6} md={3}>
      <Card variant="outlined">
        <CardContent>
          <Typography variant="caption" color="text.secondary">
            {label.toUpperCase()}
          </Typography>
          <Typography variant="h5">{value}</Typography>
          {hint ? (
            <Typography variant="caption" color="warning.main">
              {hint}
            </Typography>
          ) : null}
        </CardContent>
      </Card>
    </Grid>
  )
}

function Rows({
  headers,
  rows,
  empty,
}: {
  headers: string[]
  rows: React.ReactNode[][]
  empty?: string
}) {
  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        {empty ?? 'Nothing here yet.'}
      </Typography>
    )
  }
  return (
    <Table size="small">
      <TableHead>
        <TableRow>
          {headers.map((header) => (
            <TableCell key={header}>{header}</TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row, index) => (
          <TableRow key={index} hover>
            {row.map((cell, cellIndex) => (
              <TableCell key={cellIndex}>{cell}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}


function DecideDialog({
  quotation,
  onClose,
  onDone,
}: {
  quotation: PortalQuotation | null
  onClose: () => void
  onDone: () => void
}) {
  const [remarks, setRemarks] = useState('')
  const [busy, setBusy] = useState(false)
  const [confirming, setConfirming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const decide = async (accepted: boolean) => {
    if (!quotation) return
    setBusy(true)
    setError(null)
    try {
      await portalApi.post(`/quotations/${quotation.id}/decision`, { accepted, remarks })
      onDone()
      onClose()
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not record that just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={Boolean(quotation)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{quotation?.quotationNumber}</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography variant="body2" gutterBottom>
          {quotation?.eventType} on {quotation ? formatDate(quotation.eventDate) : ''} for{' '}
          {quotation?.guestCount} guests — <strong>{formatMoney(quotation?.totalAmount ?? 0)}</strong>
        </Typography>
        <Button
          size="small"
          startIcon={<DownloadIcon />}
          sx={{ mt: 1 }}
          onClick={() => quotation && void openPortalPdf(`/quotations/${quotation.id}/pdf`)}
        >
          Read the full quotation
        </Button>
        <Alert severity="info" sx={{ mt: 2 }}>
          Please read the quotation before you accept. Accepting confirms these items, this
          number of guests and this price.
        </Alert>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Anything to add"
          value={remarks}
          onChange={(event) => setRemarks(event.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button color="error" disabled={busy} onClick={() => void decide(false)}>
          Decline
        </Button>
        <Button variant="contained" disabled={busy} onClick={() => setConfirming(true)}>
          Accept
        </Button>
      </DialogActions>

      {/* Accepting is an agreement to a price, so it is asked twice. */}
      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Accept this quotation?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            You are agreeing to {quotation?.eventType} on{' '}
            {quotation ? formatDate(quotation.eventDate) : ''} for {quotation?.guestCount} guests at{' '}
            <strong>{formatMoney(quotation?.totalAmount ?? 0)}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Go back</Button>
          <Button
            variant="contained"
            disabled={busy}
            onClick={() => {
              setConfirming(false)
              void decide(true)
            }}
          >
            Yes, accept
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

/** Rating an event that has happened. */
function FeedbackDialog({
  order,
  onClose,
  onDone,
}: {
  order: PortalOrder | null
  onClose: () => void
  onDone: () => void
}) {
  const [scores, setScores] = useState<Record<string, number | null>>({})
  const [comments, setComments] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const questions: [string, string][] = [
    ['foodQuality', 'The food'],
    ['taste', 'The taste'],
    ['service', 'The service'],
    ['staffBehaviour', 'Our staff'],
    ['timeliness', 'Keeping to time'],
    ['cleanliness', 'Cleanliness'],
    ['valueForMoney', 'Value for money'],
  ]

  const submit = async () => {
    if (!order) return
    setBusy(true)
    setError(null)
    try {
      const body: Record<string, unknown> = { comments: comments || undefined }
      questions.forEach(([key]) => {
        if (scores[key]) body[key] = scores[key]
      })
      await portalApi.post(`/orders/${order.id}/feedback`, body)
      onDone()
      onClose()
    } catch (problem) {
      setError(problem instanceof ApiError ? problem.message : 'Could not send that just now.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog open={Boolean(order)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>How did we do?</DialogTitle>
      <DialogContent>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {order?.eventType} on {order ? formatDate(order.eventDate) : ''}
        </Typography>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {questions.map(([key, label]) => (
            <Stack key={key} direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="body2">{label}</Typography>
              <Rating
                value={scores[key] ?? null}
                onChange={(_event, value) => setScores((current) => ({ ...current, [key]: value }))}
              />
            </Stack>
          ))}
        </Stack>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="Anything else you would like to tell us"
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Not now</Button>
        <Button variant="contained" disabled={busy} onClick={() => void submit()}>
          {busy ? 'Sending…' : 'Send'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
